import React, { useState } from "react";
import styles from "../Table.module.css";
import Select from "react-select";
import { useSelector } from "react-redux";
import { getAllLabeledStock } from "services/customerTransaction";
import useDebounce from "hooks/useDebounce";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";

const selectStyle1 = {
  control: (base) => ({
    ...base,
    height: "38px",
    width: "100%",
    minHeight: "32px",
    zIndex: "10",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#aaa",
    fontStyle: "italic",
    display: "none",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "blue"
      : state.isFocused
      ? "lightblue"
      : "white",
    color: state.isSelected ? "white" : "black",
    padding: "6px",
    fontSize: "12px",
    whiteSpace: "normal", // Allows text to wrap instead of truncating
    textOverflow: "unset", // Removes ellipsis
    overflow: "visible", // Ensures full visibility of text
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
    padding: "2px 2px 2px 0px", // Decreases padding inside the input box
    whiteSpace: "normal", // Allows full text to display
    textOverflow: "unset", // Removes ellipsis
    overflow: "visible", // Ensures full visibility of text
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    marginTop: "5px",
    zIndex: 999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "100px", // Ensures the dropdown has a scrollable height
    overflowY: "auto", // Enables scrolling for overflow
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    svg: {
      width: "12px",
      height: "12px",
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0px",
  }),
};

const headers = [
  "Sr.No.",
  "Item Details",
  "T. Wt",
  "Gr Wt.",
  "St Wt",
  "D. Wt",
  "Nt Wt",
  "Fine %",
  "Wastage %",
  "F + W Wt.",
  "Rate/Gm",
  "St Amt",
  "Qty",
  "Pieces",
  "Hallmark Amt",
  "T Item Amt",
  "Packing Wt",
  "Dia Wt",
  "MRP",
  "URD Amt",
];

const selectStyle = {
  control: (base) => ({
    ...base,
    minHeight: "20px",
    height: "25px",
    width: "150px",
    zIndex: "10",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "blue"
      : state.isFocused
      ? "lightblue"
      : "white",
    color: state.isSelected ? "white" : "black",
    padding: "6px",
    fontSize: "12px",
    whiteSpace: "normal",
    textOverflow: "unset",
    overflow: "visible",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
    padding: "2px 2px 2px 0px",
    whiteSpace: "normal",
    textOverflow: "unset",
    overflow: "visible",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    marginTop: "5px",
    zIndex: 999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "100px",
    overflowY: "auto",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    svg: {
      width: "12px",
      height: "12px",
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0px",
  }),
};

const IssueCustomerTransaction = ({ selectedCustomer, onIssueDataChange }) => {
  const [issueList, setIssueList] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [itemCode, setItemCode] = useState("");
  const debouncedItemCode = useDebounce(itemCode, 500);
  const adminLoggedIn = useSelector((state) => state.reducer1);
  const clientCode = adminLoggedIn.ClientCode;

  const [totals, setTotals] = useState({
    totalWeight: "0",
    totalGrossWeight: "0",
    totalStoneWeight: "0",
    totalDiamondWeight: "0",
    totalNetWeight: "0",
    totalFineWastagePercentage: "0",
    totalFineWastageWeight: "0",
    totalQuantity: "0",
  });

  const { data: labeledStockList = [], isLoading } = useQuery({
    queryKey: ["labeledStock", clientCode, debouncedItemCode],
    queryFn: async () => {
      if (!debouncedItemCode?.trim()) return [];
      try {
        const response = await getAllLabeledStock({
          payload: {
            ClientCode: clientCode,
            ItemCode: debouncedItemCode,
          },
        });
        return response?.filter((item) => item?.Status === "Active") || [];
      } catch (error) {
        console.error("Error fetching labeled stock:", error);
        toast.error("Failed to load items. Please try again.");
        return [];
      }
    },
    enabled: !!debouncedItemCode?.trim(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const handleItemSelect = (data) => {
    if (!selectedCustomer) {
      toast.error("Please select a customer first");
      setItemCode("");
      setSelectedOption(null);
      return;
    }

    if (data?.item) {
      const isAvailable = issueList.some(
        (item) => item.ItemCode === data.item.ItemCode
      );

      if (isAvailable) {
        toast.error("Item already added");
        setItemCode("");
        setSelectedOption(null);
        return;
      }

      const newItem = {
        ItemCode: data.item.ItemCode,
        CategoryName: data.item.CategoryName,
        CategoryId: data.item.CategoryId,
        ProductId: data.item.ProductId,
        DesignId: data.item.DesignId,
        PurityId: data.item.PurityId,
        CustomerId: selectedCustomer.Id,
        MetalRate: data.item.MetalRate,
        LotNumber: data.item.LotNumber,
        SKUId: data.item.SKUId,
        PackingWeight: data.item.PackingWeight,
        StoneWeight: data.item.StoneWeight,
        DiamondWeight: data.item.DiamondWeight,
        ProductName: data.item.ProductName,
        PurityName: data.item.PurityName,
        TotalWeight: data.item.TotalWeight.toString() || "0",
        GrossWt: data.item.GrossWt || "0",
        TotalStoneWeight: data.item.TotalStoneWeight || "0",
        DiamondWeight: data.item.DiamondWeight || "0",
        NetWt: data.item.NetWt || "0",
        FinePercent: data.item?.FinePercent + data.item?.WastagePercent || "0",
        FineWastageWt: data.item.FineWastageWt || "0",
        Qty: data.item.Qty || "1",
      };

      const updatedIssueList = [...issueList, newItem];
      setIssueList(updatedIssueList);
      setItemCode("");
      setSelectedOption(null);

      onIssueDataChange?.(updatedIssueList);

      setTotals((prev) => ({
        totalWeight: (
          parseFloat(prev.totalWeight) + parseFloat(newItem.TotalWeight)
        ).toFixed(3),
        totalGrossWeight: (
          parseFloat(prev.totalGrossWeight) + parseFloat(newItem.GrossWt)
        ).toFixed(3),
        totalStoneWeight: (
          parseFloat(prev.totalStoneWeight) +
          parseFloat(newItem.TotalStoneWeight)
        ).toFixed(3),
        totalDiamondWeight: (
          parseFloat(prev.totalDiamondWeight) +
          parseFloat(newItem.DiamondWeight || 0)
        ).toFixed(3),
        totalNetWeight: (
          parseFloat(prev.totalNetWeight) + parseFloat(newItem.NetWt)
        ).toFixed(3),
        totalFineWastagePercentage: (
          parseFloat(prev.totalFineWastagePercentage) +
          parseFloat(newItem.FinePercent)
        ).toFixed(2),
        totalFineWastageWeight: (
          parseFloat(prev.totalFineWastageWeight) +
          parseFloat(newItem.FineWastageWt)
        ).toFixed(3),
        totalQuantity: (
          parseFloat(prev.totalQuantity) + parseFloat(newItem.Qty)
        ).toFixed(0),
      }));
    }
  };

  const selectOptions = labeledStockList.map((item) => ({
    value: item.Id,
    label: `${item.ItemCode} - ${item.SKU} - ${item.CategoryName} - ${item.ProductName} - ${item.PurityName}`,
    item: item,
  }));

  return (
    <div>
      <div
        style={{
          background: "rgba(224, 224, 224, 0.88)",
          height: "31px",
          borderRadius: "5px",
          textAlign: "left",
          padding: "12px 0px 0px 29px",
          fontSize: "14px",
        }}
      >
        <span
          style={{
            background: "rgba(224, 224, 224, 0.88)",
            color: "black",
            fontWeight: "bold",
            color: "#495057",
          }}
        >
          Sample Out
        </span>
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {issueList.map((issue, index) => (
              <tr key={issue.id || index}>
                <td>{index + 1}</td>
                <td>
                  <div>
                    {issue.ItemCode} - {issue.SKU} - {issue.CategoryName} -{" "}
                    {issue.ProductName} - {issue.PurityName}
                  </div>
                </td>
                <td>{issue.TotalWeight}</td>
                <td>{issue.GrossWt}</td>
                <td>{issue.TotalStoneWeight}</td>
                <td>{issue.DiamondWeight}</td>
                <td>{issue.NetWt}</td>
                <td>{issue.FinePercent}</td>
                <td>{issue.FineWastageWt}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
                <td>{issue.Qty}</td>
              </tr>
            ))}
            <tr>
              <td>{issueList.length + 1}</td>
              <td>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Select
                    options={selectOptions}
                    value={selectedOption}
                    inputValue={itemCode}
                    onInputChange={setItemCode}
                    onChange={handleItemSelect}
                    isSearchable={true}
                    isLoading={isLoading}
                    styles={selectStyle}
                    placeholder="Search items..."
                  />
                </div>
              </td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
            </tr>
            <tr className={styles.totalRow}>
              <td></td>
              <td>Total</td>
              <td>{totals.totalWeight}</td>
              <td>{totals.totalGrossWeight}</td>
              <td>{totals.totalStoneWeight}</td>
              <td>{totals.totalDiamondWeight}</td>
              <td>{totals.totalNetWeight}</td>
              <td>{totals.totalFineWastagePercentage}</td>
              <td>{totals.totalFineWastageWeight}</td>
              <td>{totals.totalQuantity}</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IssueCustomerTransaction;
